/* -----------------------------------
Copyright: Logical Developments 2025.
Project:   ConNote Portal
Filename:  AddVendor.js
Author:    jdkohl
Version:   0.02
Description:
Multi-step vendor addition workflow. Customer creates vendor by entering
name, accepting terms, nominating addresses, then receives code to share
with vendor for activation.

History:
0.02  10-12-25 JDK   Refactored to multi-step workflow with context
0.01  28-10-25 JDK   Implemented two-step vendor verification workflow
0.00  19-08-25 JDK   Created.
----------------------------------- */

import { useState, useEffect, useCallback } from 'react';
import { AddVendorProvider, useAddVendorContext } from './AddVendorContext.js';
import EnterName from './EnterName.js';
import AcceptTerms from './AcceptTerms.js';
import Addresses from './Addresses.js';
import CopyCode from './CopyCode.js';
import useAuthenticate from '#root/src/Session/useAuthenticate.js';
import { omnisUrl } from '#root/src/Configuration/Config.js';
import '#src/Settings/Settings.css'

// Step constants
const STEPS = {
  ENTER_NAME: 1,
  ACCEPT_TERMS: 2,
  ADDRESSES: 3,
  COPY_CODE: 4
};

// Inner component that uses context
function AddVendorInner({ myAddresses, onVendorAdded, onValidationChange, onError }) {
  const { customer } = useAuthenticate();
  const { vendor, updateVendor } = useAddVendorContext();

  const [currentStep, setCurrentStep] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Save vendor to API and advance to copy code step
   */
  const handleSaveVendor = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${omnisUrl}/vendor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: customer?.token,
          vendor
        })
      });

      if (response.ok) {
        const savedVendor = await response.json();
        onError('');
        onVendorAdded(savedVendor); // Parent unmounts this component after this call
      } else {
        const errorBody = await response.text();
        onError(errorBody || response.statusText);
        setIsSubmitting(false);
      }
    } catch (error) {
      onError(error.message);
      setIsSubmitting(false);
    }
  }, [customer?.token, vendor, onVendorAdded, onError]);

  /**
   * Reset workflow and return to step 1
   */
  const handleDone = useCallback(() => {
    updateVendor({ type: 'reset' });
    setCurrentStep(1);
    setTermsAccepted(false);
  }, [updateVendor]);

  /**
   * Handle next button click
   */
  const handleNext = useCallback(async (enabled) => {
    if (!enabled) return;
    if (currentStep === 3) {
      await handleSaveVendor();
    } else if (currentStep === 4) {
      handleDone();
    } else {
      // Capture terms acceptance when leaving Step 2
      if (currentStep === 2 && termsAccepted) {
        updateVendor({ type: 'key', key: 'termsAccepted', value: true });
        updateVendor({ type: 'key', key: 'termsAcceptanceTS', value: new Date().toISOString() });
      }
      // If the current step is 2 (Terms & Conditions), only allow incrementing the current step if the terms have been accepted.
      if (currentStep !== 2 || (currentStep === 2 && termsAccepted)) setCurrentStep(currentStep + 1);
    }
  }, [currentStep, handleSaveVendor, handleDone, termsAccepted, updateVendor]);

  /**
   * Update parent with validation state whenever dependencies change
   */
  useEffect(() => {
    const isValid =
      (currentStep === 1 && vendor?.name?.trim() !== '') ||
      (currentStep === 2 && termsAccepted) ||
      (currentStep === 3 && vendor?.addresses?.length > 0) ||
      currentStep === 4;

    const buttonText =
      currentStep === 1 || currentStep === 2 ? "Next" :
      currentStep === 3 ? "Save" : "Done";

    onValidationChange({
      canProceed: isValid && !isSubmitting,
      buttonText,
      onNext: handleNext
    });
  }, [currentStep, vendor?.name, termsAccepted, vendor?.addresses?.length, isSubmitting, handleNext, onValidationChange]);

  /**
   * Render current step component
   */
  const renderStep = () => {
    switch (currentStep) {
      case STEPS.ENTER_NAME:
        return <EnterName />;
      case STEPS.ACCEPT_TERMS:
        return <AcceptTerms
          termsAccepted={termsAccepted}
          setTermsAccepted={setTermsAccepted}
        />;
      case STEPS.ADDRESSES:
        return <Addresses myAddresses={myAddresses} />;
      case STEPS.COPY_CODE:
        return <CopyCode />;
      default:
        return null;
    }
  };

  return (
    <div className="stack">
      {renderStep()}
    </div>
  );
}

// Outer component that provides context
function AddVendor({ myAddresses, onVendorAdded, onValidationChange, onError }) {
  return (
    <AddVendorProvider>
      <AddVendorInner
        myAddresses={myAddresses}
        onVendorAdded={onVendorAdded}
        onValidationChange={onValidationChange}
        onError={onError}
      />
    </AddVendorProvider>
  );
}

export default AddVendor
